import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Receipt, Trash2, ImageIcon, Pencil, Loader2 } from "lucide-react";
import { api, receiptImageUrl } from "@/lib/api";
import { formatMoney } from "@/lib/money";

const DEFAULT_CATEGORIES = ["Travel", "Gas", "Miscellaneous", "Lodging", "Food"];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function EditDialog({ expense, open, onOpenChange, onSaved }) {
  const [description, setDescription] = useState(expense?.description || "");
  const [amount, setAmount] = useState(expense?.amount != null ? String(expense.amount) : "");
  const [notes, setNotes] = useState(expense?.notes || "");
  const [category, setCategory] = useState(expense?.category || "Miscellaneous");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (expense) {
      setDescription(expense.description || "");
      setAmount(expense.amount != null ? String(expense.amount) : "");
      setNotes(expense.notes || "");
      setCategory(expense.category || "Miscellaneous");
    }
  }, [expense]);

  if (!expense) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/expenses/${expense.id}`, {
        description: description.trim(),
        amount: parseFloat(amount),
        notes,
        category,
      });
      toast.success("Expense updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-semibold tracking-tight">Edit expense</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="edit-description-input" />
          </div>
          <div className="space-y-2">
            <Label>Amount</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="edit-amount-input" />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="edit-category-select-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEFAULT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-full" data-testid="edit-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptThumb({ receiptId }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let obj;
    if (!receiptId) return;
    (async () => {
      try {
        const src = receiptImageUrl(receiptId);
        const res = await fetch(src, { credentials: "include" });
        if (!res.ok) throw new Error("bad");
        const blob = await res.blob();
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      } catch { setErr(true); }
    })();
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [receiptId]);
  if (!receiptId || err) {
    return (
      <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center text-muted-foreground">
        <ImageIcon className="w-4 h-4" />
      </div>
    );
  }
  return (
    <div className="w-12 h-12 rounded-md overflow-hidden bg-muted border border-border/60">
      {url ? <img src={url} alt="Receipt" className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse bg-muted" />}
    </div>
  );
}

export default function ExpenseList({ loading, expenses, onNew, onDeleted, onUpdated }) {
  const [editing, setEditing] = useState(null);

  const remove = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <Card className="p-12 border-border/60 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading expenses…
      </Card>
    );
  }

  if (!expenses.length) {
    return (
      <Card className="p-12 border-border/60 flex flex-col items-center justify-center text-center gap-4" data-testid="expense-list-empty">
        <div className="w-14 h-14 rounded-full bg-accent/60 flex items-center justify-center text-primary">
          <Receipt className="w-6 h-6" />
        </div>
        <div className="space-y-1">
          <div className="font-display text-lg font-semibold tracking-tight">No expenses this quarter</div>
          <div className="text-sm text-muted-foreground max-w-sm">Snap your first receipt and we&apos;ll track it toward the total your company owes you.</div>
        </div>
        <Button onClick={onNew} className="rounded-full mt-2" data-testid="empty-new-expense-btn">Add first expense</Button>
      </Card>
    );
  }

  return (
    <Card className="border-border/60 overflow-hidden" data-testid="expense-list-card">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="w-16"></TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.map((e, idx) => (
              <motion.tr
                key={e.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03, duration: 0.25 }}
                className="border-b border-border/40 hover:bg-secondary/40 transition-colors"
                data-testid={`expense-row-${e.id}`}
              >
                <TableCell className="py-3"><ReceiptThumb receiptId={e.receipt_id} /></TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(e.expense_date)}</TableCell>
                <TableCell className="font-medium">{e.description}</TableCell>
                <TableCell>
                  {e.category ? (
                    <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 rounded-full bg-secondary text-secondary-foreground border border-border/60" data-testid={`expense-category-${e.id}`}>
                      {e.category}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{e.notes}</TableCell>
                <TableCell className="text-right font-display font-semibold tabular-nums text-primary" data-testid={`expense-amount-${e.id}`}>
                  {formatMoney(e.amount)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(e)} data-testid={`edit-btn-${e.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-btn-${e.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes the receipt from your quarterly total. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(e.id)} data-testid={`confirm-delete-${e.id}`}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </div>
      <EditDialog expense={editing} open={!!editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={onUpdated} />
    </Card>
  );
}
